import { useEffect, useState } from "react";

import { useDrop } from "react-dnd";

import LeadEvent from "./LeadEvent";
import useTrips from "../../data/useTrips";
import classNames from "../../utils/classNames";
import { ICalendarTrip } from "../../models/Trip";
import { DragTypes } from "../../models/DragTypes";
import { IAllLeadsResponse, ILead, LeadStatus, PaymentStatus } from "../../models/Lead";
import { Distances } from "../../pages/Calendar/TripManager";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";

type IProps = {
  trip: ICalendarTrip;
  date: string;
  capacity: number;
  distances: Distances | undefined;
  selectedTrip?: string;
  setSelectedTrip?: any;
  handleSelected?: any;
  year?: number;
  week?: number;
  leadsFromIds: IAllLeadsResponse | undefined;
  searchTerm?: string;
  showCollectedFilter?: boolean;
  showNotPaidFilter?: boolean;
  tripColour?: string;
  // The pickup-number badge and trip-colour border are optimisation visuals, so
  // they only show when the Trip Optimisation feature flag is enabled.
  optimisationEnabled?: boolean;
};

export default function ThirdPartyTripSlot(props: IProps) {
  const { update: updateTrip } = useTrips(props.year!, props.week!);
  const { saveHandlers } = useDefaultCRUDHandlers("Lead");
  const [leads, setLeads] = useState<ILead[]>([]);

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: DragTypes.EVENT,
      drop: (e: ILead) => handleDroppedLead(e),
      collect: (monitor) => ({
        isOver: !!monitor.isOver(),
      }),
    }),
    [props.trip, leads, props.date]
  );

  const handleDroppedLead = (lead: ILead) => {
    if (lead === undefined) {
      saveHandlers.onError();
      return;
    }

    let updatedTrip = {
      leadId: lead.id,
      tripId: props.trip.id,
      driverId: props.trip.driverId,
      scheduledCollectionOn: props.date,
      order: leads.length,
    };

    updateTrip.mutate(updatedTrip, {
      onSuccess: () => {
        props.handleSelected(undefined);
        saveHandlers.onSuccess();
      },
      onError: saveHandlers.onError,
    });
  };

  const handleColour = () => {
    let background = "bg-gray-50";

    if (props.trip.id == props.selectedTrip) background = "bg-green-200";
    if (isOver) background = "bg-primary-200";

    return background;
  };

  const handleOrderChange = (up: boolean, lead: ILead) => {
    const newOrder = up ? lead.order - 1 : lead.order + 1;
    if (newOrder < 0 || newOrder > leads.length - 1) return;

    const leadInNewOrder = leads.find((l: ILead) => l.order === newOrder);
    if (leadInNewOrder === undefined) return;

    updateTrip.mutate(
      {
        leadId: lead.id,
        tripId: props.trip.id,
        driverId: props.trip.driverId,
        scheduledCollectionOn: props.date,
        order: newOrder,
      },
      {
        onSuccess: () => {
          updateTrip.mutate(
            {
              leadId: leadInNewOrder.id,
              tripId: props.trip.id,
              driverId: props.trip.driverId,
              scheduledCollectionOn: props.date,
              order: lead.order,
            },
            saveHandlers
          );
        },
        onError: saveHandlers.onError,
      }
    );
  };

  const leadMatchesFilters = (lead: ILead) => {
    if (!props.showCollectedFilter && !props.showNotPaidFilter) {
      return true;
    }

    const matchesCollected = !props.showCollectedFilter || !!lead.vehicle?.collectedOn;
    const matchesNotPaid =
      !props.showNotPaidFilter || lead.paymentStatus !== PaymentStatus.Sent;

    return matchesCollected && matchesNotPaid;
  };

  useEffect(() => {
    const allLeads = props.leadsFromIds?.data?.leads ?? [];
    const tripLeadIds = props.trip.leads ?? [];

    if (tripLeadIds.length === 0 || allLeads.length === 0) {
      setLeads([]);
      return;
    }

    const inTrip = allLeads.filter(
      (lead: ILead) =>
        tripLeadIds.includes(lead.id) &&
        lead.status !== LeadStatus.Completed &&
        !lead.completedOn
    );
    setLeads(
      inTrip.sort((a: ILead, b: ILead) => (a.order > b.order ? 1 : -1))
    );
  }, [props.leadsFromIds?.data, props.trip.leads, props.trip.id, props.date]);

  // Cancelled leads aren't collected, so they don't take a pickup slot/number.
  const activePositionById = new Map<string, number>();
  let activeCounter = 0;
  leads.forEach((lead: ILead) => {
    if (lead.status !== LeadStatus["Cancelled"]) {
      activeCounter += 1;
      activePositionById.set(lead.id, activeCounter);
    }
  });

  return (
    <div
      className={classNames(handleColour(), "w-60 relative p-1 h-full min-h-[11rem] border-r border-gray-200")}
      onClick={() => props.setSelectedTrip(undefined, props.trip.id)}
      ref={drop}
      style={{
        opacity: isOver ? 0.8 : 1,
      }}
    >
      <div className="h-full flex flex-col justify-between text-right">
        <div>
          {leads.map((lead: ILead) => (
            <div
              key={lead.id}
              className={classNames(
                leadMatchesFilters(lead) ? "" : "opacity-35 grayscale"
              )}
            >
              <LeadEvent
                lead={lead}
                distances={props.distances}
                handleSelected={() => { }}
                inGroup={leads.length}
                searchTerm={props.searchTerm}
                handleOrderChange={handleOrderChange}
                tripColour={props.optimisationEnabled ? props.tripColour : undefined}
                displayOrder={
                  props.optimisationEnabled ? activePositionById.get(lead.id) : undefined
                }
              />
            </div>
          ))}
        </div>
        <span className="w-full px-2 text-gray-400">{`${activeCounter} / ${props.capacity}`}</span>
      </div>
    </div>
  );
}
